import { makeAutoObservable, runInAction } from 'mobx';
import { SortType } from '../../../../constants/tool-order';
import { exportMultipleSheetExcel } from '../../../../constants/tool.fn';
import { applyClientTableSort, EFFICIENCY_ANALYSIS_SORT_COLUMN_TYPES } from '../dish-data-table-sort.utils';
import { dateFormat } from '../../../../constants/utils';
import i18n from '../../../../i18n';
import { LOCAL_STORAGE_KEYS } from '../../../../modules/cache/local-storage.constant';
import LSM from '../../../../modules/cache/tool-local-storage';
import apiManage from '../../../../request';
import { DishCategoryItem } from '../../../../request/api/data-analysis';
import { FetchCancelGate } from '../../../../utils/fetch-cancel-gate';
import dataAnalysisStore from '../../data-analysis-state';
import { DishDataSelectOption, EfficiencyAnalysisRow } from './dish-data-shared.types';

/**
 * 出菜率分析状态。
 */
export class EfficiencyAnalysisState {
    /** 加载态。 */
    loading = false;

    /** 错误文案内容。 */
    errorMessage = '';

    /** 列表数据。 */
    list: EfficiencyAnalysisRow[] = [];

    /** 一级菜单选中值。 */
    mainGroupId = '-1';

    /** 二级菜单选中值。 */
    secondMenuId = '-1';

    /** 一级菜单选项。 */
    mainGroups: DishDataSelectOption[] = [{ value: '-1', label: 'business_analysis_page_text_all' }];

    /** 一级菜单原始数据。 */
    mainGroupsOriginData: DishCategoryItem[] = [];

    /** 当前餐厅 string_id。 */
    currentRestaurantStringId = '';

    /** 搜索关键词。 */
    searchKeyword = '';

    /** 是否为多选模式，当前网页默认保持多选。 */
    multiMode = true;

    /** 当前选中的行 key。 */
    selectedKeys: string[] = [];

    /** 表格前端排序状态。 */
    sortKeyValue: SortType = {};

    /** 列表请求取消门闩（日期/筛选快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();

    /** 菜单筛选请求序号，用于忽略过期响应。 */
    private filterRequestSeq = 0;

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * 设置一级菜单。
     * @param value - 一级菜单值
     */
    setMainGroupId(value: string) {
        this.mainGroupId = value;
        this.secondMenuId = '-1';
        this.selectedKeys = [];
    }

    /**
     * 设置二级菜单。
     * @param value - 二级菜单值
     */
    setSecondMenuId(value: string) {
        this.secondMenuId = value;
        this.selectedKeys = [];
    }

    /**
     * 设置当前餐厅，并在切餐厅时清空餐厅相关筛选。
     * @param value - 当前餐厅 string_id
     */
    setRestaurantStringId(value: string) {
        if (this.currentRestaurantStringId === value) {
            return;
        }
        this.currentRestaurantStringId = value;
        this.searchKeyword = '';
        this.mainGroupId = '-1';
        this.secondMenuId = '-1';
        this.mainGroupsOriginData = [];
        this.mainGroups = [{ value: '-1', label: 'business_analysis_page_text_all' }];
        this.selectedKeys = [];
    }

    /**
     * 设置搜索关键词。
     * @param value - 搜索关键词
     */
    setSearchKeyword(value: string) {
        this.searchKeyword = value;
    }

    /**
     * 设置表格前端排序。
     * @param value - 排序列与方向
     */
    setSortValue(value: SortType) {
        this.sortKeyValue = value;
    }

    /**
     * 切换单行的选中状态，用于与菜品销售分析保持一致的行点击交互。
     * @param row - 当前点击的行
     */
    toggleRowSelection(row: EfficiencyAnalysisRow) {
        /** 当前行是否已被选中。 */
        const isSelected = this.selectedKeys.includes(row.rowKey);
        this.selectedKeys = isSelected
            ? this.selectedKeys.filter((item) => item !== row.rowKey)
            : [...this.selectedKeys, row.rowKey];
    }

    /**
     * 设置多选模式。
     * @param value - 是否开启多选
     */
    setMultiMode(value: boolean) {
        this.multiMode = value;
        if (!value) {
            this.selectedKeys = this.selectedKeys.slice(0, 1);
        }
    }

    /**
     * 切换多选模式。
     */
    toggleMultiMode() {
        this.setMultiMode(!this.multiMode);
    }

    /**
     * 切换单行勾选状态。
     * @param rowKey - 行 key
     * @param checked - 是否勾选
     */
    toggleRow(rowKey: string, checked: boolean) {
        if (!this.multiMode) {
            this.selectedKeys = checked ? [rowKey] : [];
            return;
        }
        this.selectedKeys = checked
            ? this.selectedKeys.includes(rowKey) ? this.selectedKeys : [...this.selectedKeys, rowKey]
            : this.selectedKeys.filter((item) => item !== rowKey);
    }

    /**
     * 用表格选中行同步内部 key 列表。
     * @param selectedRows - 选中行对象
     */
    setSelectedRows(selectedRows: EfficiencyAnalysisRow[]) {
        this.selectedKeys = selectedRows.map((item) => item.rowKey);
    }

    /**
     * 设置单选模式下的当前选中行。
     * @param row - 当前点击的行
     */
    setSingleSelectedRow(row: EfficiencyAnalysisRow) {
        this.selectedKeys = [row.rowKey];
    }

    /**
     * 清空当前选择态。
     */
    clearSelection() {
        this.selectedKeys = [];
    }

    /**
     * 拉取一级/二级菜单筛选项。
     * @param stringId - 餐厅 string_id
     */
    async fetchFilters(stringId: string) {
        /** 当前请求序号。 */
        const requestId = ++this.filterRequestSeq;
        const response = await apiManage.get_data_analysis_res_dish_Category(stringId);
        runInAction(() => {
            if (requestId !== this.filterRequestSeq || this.currentRestaurantStringId !== stringId) {
                return;
            }
            if (response?.error || !Array.isArray(response)) {
                this.mainGroupsOriginData = [];
                this.mainGroups = [{ value: '-1', label: 'business_analysis_page_text_all' }];
                return;
            }
            this.mainGroupsOriginData = response;
            this.mainGroups = [
                { value: '-1', label: 'business_analysis_page_text_all' },
                ...response.map((item) => ({
                    value: `${item.main_group_id ?? ''}`,
                    label: item.main_group_name ?? '',
                })),
            ];
        });
    }

    /**
     * 拉取出菜率列表。
     * @param stringId - 餐厅 string_id
     */
    async fetchData(stringId: string) {
        if (this.currentRestaurantStringId !== stringId) {
            this.setRestaurantStringId(stringId);
        }
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.loading = true;
        this.errorMessage = '';
        this.list = [];
        this.selectedKeys = [];
        this.sortKeyValue = {};
        try {
            const response = await apiManage.get_data_analysis_service_dish_efficiency_period({
                string_id: stringId,
                start: dataAnalysisStore.startDate,
                end: dataAnalysisStore.endDate,
                main_group_id: this.mainGroupId === '-1' ? undefined : this.mainGroupId,
                dmi_slu_id: this.secondMenuId === '-1' ? undefined : this.secondMenuId,
            }, { cancelToken });
            runInAction(() => {
                if (!this.listFetchGate.isLatest(seq)) {
                    return;
                }
                if (response?.error || !Array.isArray(response)) {
                    this.list = [];
                    this.errorMessage = i18n.t('business_analysis_page_text_restaurant_data_not_synchronized');
                    return;
                }
                /** 列表行补充 rowKey；不再写入 `totalMaxRate`。 */
                this.list = response.map((item, index) => ({
                    ...item,
                    rowKey: `efficiency-${index}-${item.menu_item_name ?? item.item_name2 ?? ''}`,
                }));
                this.syncSelection();
            });
        } finally {
            runInAction(() => {
                this.listFetchGate.endLoading(seq, (value) => {
                    this.loading = value;
                });
            });
        }
    }

    /**
     * 是否按当前勾选行导出（无勾选时导出筛选后全部行）。
     * @returns 为 true 时导出 `selectedRows`
     */
    get exportUsesSelectedRows() {
        return this.selectedKeys.length > 0;
    }

    /**
     * 参与导出的行：有勾选则导出勾选行，否则导出当前筛选后全部行。
     * @returns 导出行列表
     */
    get rowsForExport() {
        return this.exportUsesSelectedRows ? this.selectedRows : this.filteredRows;
    }

    /**
     * 导出出菜率列表（勾选行或筛选后全部行）。
     */
    exportData() {
        /** 默认餐厅信息。 */
        const defaultRestaurant = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT) || {};
        /** 待导出行。 */
        const exportRows = this.rowsForExport;
        /** 表格导出内容。 */
        const sheetData = [{
            sheetName: i18n.t('business_analysis_page_analysis_efficiency_dishes'),
            data: [[
                i18n.t('business_analysis_page_dishes_name_one'),
                i18n.t('business_analysis_page_dishes_name_two'),
                i18n.t('business_analysis_page_text_sum_quantity'),
                i18n.t('business_analysis_page_average_time_minute'),
            ]].concat(exportRows.map((item) => [
                item.menu_item_name ?? '',
                item.item_name2 ?? '',
                String(item.quantity ?? 0),
                item.average_time != null ? (Number(item.average_time) / 60).toFixed(2) : '',
            ])),
            wpx: [{ wpx: 180 }, { wpx: 180 }, { wpx: 120 }, { wpx: 140 }],
        }];
        /** 导出开始时间。 */
        const startText = dateFormat(dataAnalysisStore.startDate, 'YYYY-MM-DD HH-mm');
        /** 导出结束时间。 */
        const endText = dateFormat(dataAnalysisStore.endDate, 'YYYY-MM-DD HH-mm');
        exportMultipleSheetExcel(
            `${i18n.t('business_analysis_page_analysis_efficiency_dishes')} ${defaultRestaurant.nickname ?? ''} ${startText}-${endText}`,
            sheetData
        );
    }

    /**
     * 获取当前一级菜单下的二级菜单选项。
     * @returns 二级菜单选项
     */
    get secondMenuOptions() {
        if (this.mainGroupId === '-1') {
            return [{ value: '-1', label: 'business_analysis_page_text_all' }];
        }
        /** 当前一级菜单项。 */
        const currentGroup = this.mainGroupsOriginData.find((item) => `${item.main_group_id ?? ''}` === this.mainGroupId);
        /** 当前一级菜单下的二级菜单列表。 */
        const menuItemSlu = currentGroup?.menu_item_slu ?? [];
        return [
            { value: '-1', label: 'business_analysis_page_text_all' },
            ...menuItemSlu.map((item) => ({
                value: `${item.dmi_slu_id ?? ''}`,
                label: item.dmi_slu_name ?? '',
            })),
        ];
    }

    /**
     * 过滤后的行列表。
     * @returns 过滤后的列表
     */
    get filteredRows() {
        /** 标准化后的搜索词。 */
        const normalizedKeyword = this.searchKeyword.trim().toLowerCase();
        /** 搜索过滤后的行。 */
        const keywordFilteredRows = !normalizedKeyword
            ? this.list
            : this.list.filter((row) => [
                row.menu_item_name,
                row.item_name2,
            ].some((value) => `${value ?? ''}`.toLowerCase().includes(normalizedKeyword)));
        return applyClientTableSort(keywordFilteredRows, this.sortKeyValue, EFFICIENCY_ANALYSIS_SORT_COLUMN_TYPES);
    }

    /**
     * 当前选中的行对象。
     * @returns 选中行
     */
    get selectedRows() {
        return this.list.filter((row) => this.selectedKeys.includes(row.rowKey));
    }

    /**
     * 是否允许打开数据对比。
     * @returns 是否允许对比
     */
    get canCompare() {
        return this.selectedRows.length >= 2;
    }

    /**
     * 当前过滤结果是否已经全部选中。
     * @returns 是否全选
     */
    get isAllFilteredSelected() {
        return this.filteredRows.length > 0
            && this.filteredRows.every((row) => this.selectedKeys.includes(row.rowKey));
    }

    /**
     * 切换“全选当前过滤结果”状态。
     */
    toggleSelectAllFilteredRows() {
        if (this.isAllFilteredSelected) {
            /** 当前过滤结果的 key 集合。 */
            const filteredKeySet = new Set(this.filteredRows.map((row) => row.rowKey));
            this.selectedKeys = this.selectedKeys.filter((key) => !filteredKeySet.has(key));
            return;
        }
        this.selectedKeys = this.filteredRows.map((row) => row.rowKey);
    }

    /**
     * 根据当前过滤结果修正选择态。
     */
    private syncSelection() {
        this.selectedKeys = this.selectedKeys.filter((key) => this.list.some((row) => row.rowKey === key));
    }
}

export default EfficiencyAnalysisState;
